'use client';

import Image from 'next/image';
import React, { useState, forwardRef, useContext, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';


import { useGetQuestionsQuery, useAddQuestionMutation } from '@shared/api';
import { formatQuestionDate, ModalContext } from '@shared/libs';
import type { Questions } from '@shared/types';
import { H2, Button, Input, ButtonSize } from '@shared/ui';

import { QuestionsPagination } from './components';
import { useQuestionsPagination } from './hooks';
import avatarImage from './image.png';
import styles from './product-questions.module.css';

interface ProductQuestionsProps {
  initialQuestions?: Questions;
  questionsPager?: { page_current: number; pages_all: number; records_all: number; records_per_page: number };
  noteId: string;
  currency: string;
  locale: {
    QUESTIONS_TITLE?: string;
    QUESTIONS_EMPTY_MESSAGE?: string;
    QUESTIONS_FORM_TITLE?: string;
    QUESTIONS_FORM_NAME_LABEL?: string;
    QUESTIONS_FORM_NAME_PLACEHOLDER?: string;
    QUESTIONS_FORM_QUESTION_PLACEHOLDER?: string;
    QUESTIONS_FORM_SUBMIT_BUTTON?: string;
    QUESTIONS_FORM_SUBMIT_LOADING?: string;
    QUESTIONS_FORM_SUCCESS_MESSAGE?: string;
    QUESTIONS_FORM_ERROR_MESSAGE?: string;
    FORM_NAME_MAX_LENGTH_ERROR?: string;
    FORM_TEXT_MAX_LENGTH_ERROR?: string;
    QUESTIONS_ANSWER_FROM?: string;
    QUESTIONS_ANONYMOUS?: string;
    PAGINATION_PREV?: string;
    PAGINATION_NEXT?: string;
  };
}

interface QuestionsSuccessContentProps {
  message: string;
  onClose?: () => void;
}

// Интерфейс для ответа мутации добавления вопроса
interface AddQuestionResponse {
  result_code: number;
  data: {
    message: string;
    question_id: number;
  };
  lang: string;
  userID: string | null;
}

const QuestionsSuccessContent = ({ message, onClose }: QuestionsSuccessContentProps) => {
  useEffect(() => {
    const timerId = setTimeout(() => {
      onClose?.();
    }, 2000);
    return () => clearTimeout(timerId);
  }, [onClose]);
  return (
    <div style={{ color: 'white' }}>
      <h3 style={{ color: 'white', margin: 0 }}>{message}</h3>
    </div>
  );
};

const ProductQuestions = forwardRef<HTMLDivElement, ProductQuestionsProps>(
  ({ initialQuestions = [], questionsPager, noteId, currency, locale }, ref) => {
    const [question, setQuestion] = useState('');
    const [name, setName] = useState('');
    const [isSubmitted] = useState(false);
    const [hasNewQuestion, setHasNewQuestion] = useState(false);
    const [nameError, setNameError] = useState('');
    const [questionError, setQuestionError] = useState('');



    // Константы для максимальной длины
    const MAX_NAME_LENGTH = 100;
    const MAX_QUESTION_LENGTH = 600;

    // Функции валидации
    const validateName = (value: string) => {
      // Валидация только если поле заполнено (поле опционально)
      if (value.trim().length > 0 && value.length > MAX_NAME_LENGTH) {
        setNameError(locale.FORM_NAME_MAX_LENGTH_ERROR || 'FORM_NAME_MAX_LENGTH_ERROR');
        return false;
      }
      setNameError('');
      return true;
    };

    const validateQuestion = (value: string) => {
      if (value.length > MAX_QUESTION_LENGTH) {
        setQuestionError(locale.FORM_TEXT_MAX_LENGTH_ERROR || 'FORM_TEXT_MAX_LENGTH_ERROR');
        return false;
      }
      setQuestionError('');
      return true;
    };

    // Сначала инициализируем пагинацию чтобы получить currentPage
    const {
      currentPage,
      setCurrentPage,
      resetToFirstPage,
    } = useQuestionsPagination(initialQuestions, questionsPager);

    // RTK Query для страниц кроме первой, или если был добавлен новый вопрос
    const { data: clientQuestionsResponse } = useGetQuestionsQuery({
      noteId,
      currency,
      page: currentPage,
      pagelen: 5,
      enabled: currentPage > 1 || hasNewQuestion, // Включаем если не первая страница или добавлен новый вопрос
    });

    const addQuestionMutation = useAddQuestionMutation();

    // Для первой страницы используем серверные данные, но если добавлен новый вопрос - RTK Query
    const questions = (currentPage === 1 && !hasNewQuestion)
      ? initialQuestions 
      : (clientQuestionsResponse?.data || initialQuestions);
    
    // Для пагинации используем актуальные данные pager из RTK Query или серверные
    const actualPager = (currentPage === 1 && !hasNewQuestion)
      ? questionsPager 
      : (clientQuestionsResponse?.pager || questionsPager);
    
    // Вычисляем параметры пагинации на основе актуальных данных
    const totalPages = actualPager?.pages_all || Math.ceil(questions.length / 5);
    const shouldShowPagination = totalPages > 1;

    const openModal = useContext(ModalContext);

    // Функция для отправки данных о вопросе в webhook
    const sendQuestionToWebhook = async (questionId: number, author: string, questionText: string, productNoteId: string) => {
      try {
        // Получаем данные о странице
        const productUrl = typeof window !== 'undefined' ? window.location.href : '';
        const productName = typeof document !== 'undefined' ? document.title : '';
        
        // Отправляем данные в наш API endpoint
        const response = await fetch('/api-v2/questions/send-chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            questionId,
            author,
            text: questionText,
            productUrl,
            productName,
            noteId: productNoteId,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result: { success: boolean; webhookResult?: unknown } = await response.json() as { success: boolean; webhookResult?: unknown };
        
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('✅ Данные о вопросе успешно отправлены в webhook:', result);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Ошибка отправки данных о вопросе в webhook:', error);
        // Не блокируем основной функционал при ошибке webhook
      }
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!question.trim()) {
        return;
      }

      // Валидация длины полей
      const isNameValid = validateName(name);
      const isQuestionValid = validateQuestion(question.trim());
      
      if (!isNameValid || !isQuestionValid) {
        return;
      }
      
      addQuestionMutation.mutate(
        {
          noteId,
          name: name.trim() || (locale.QUESTIONS_ANONYMOUS || 'QUESTIONS_ANONYMOUS'),
          text: question.trim(),
        },
        {
          onSuccess: (response: AddQuestionResponse) => {
            // Сбрасываем поля формы
            setName('');
            setQuestion('');
            // Помечаем что добавлен новый вопрос для обновления данных
            setHasNewQuestion(true);
            // Возвращаемся на первую страницу чтобы увидеть новый вопрос
            resetToFirstPage();

            // Отправляем данные о вопросе в webhook
            if (response.data?.question_id) {
              const author = name.trim() || (locale.QUESTIONS_ANONYMOUS || 'QUESTIONS_ANONYMOUS');
              void sendQuestionToWebhook(response.data.question_id, author, question.trim(), noteId);
            }

            // Показываем окно об успешной отправке, как в отзывах
            if (openModal) {
              openModal({
                modal: {
                  title: locale.QUESTIONS_TITLE || 'QUESTIONS_TITLE',
                  Content: (({ onClose }) => (
                    <QuestionsSuccessContent
                      message={locale.QUESTIONS_FORM_SUCCESS_MESSAGE || 'QUESTIONS_FORM_SUCCESS_MESSAGE'}
                      onClose={onClose}
                    />
                  )) as React.FC<{ onClose?: () => void }>,
                },
              });
            }
          },
          onError: () => {
            // eslint-disable-next-line no-alert
            alert(locale.QUESTIONS_FORM_ERROR_MESSAGE || 'QUESTIONS_FORM_ERROR_MESSAGE');
          },
        }
      );
    };

    const renderForm = () => (
      <div className={styles.questionForm}>
        <div className={styles.formBackground} />
        <div className={styles.formContent}>
          <H2 className={styles.formTitle}>
            {locale.QUESTIONS_FORM_TITLE || 'QUESTIONS_FORM_TITLE'}
          </H2>
          <form onSubmit={handleSubmit} className={`${styles.form} ${isSubmitted ? styles.formDisabled : ''}`}>
            <div className={styles.inputsWrapper}>
              <div className={styles.nameFieldWrapper}>
                <label htmlFor="name-input" className={styles.nameLabel}>
                  {locale.QUESTIONS_FORM_NAME_LABEL || 'QUESTIONS_FORM_NAME_LABEL'}
                </label>
                <Input
                  id="name-input"
                  placeholder={locale.QUESTIONS_FORM_NAME_PLACEHOLDER || 'QUESTIONS_FORM_NAME_PLACEHOLDER'}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    validateName(e.target.value);
                  }}
                  className={styles.nameInput}
                  contrast
                />
              </div>
              {nameError && (
                <div 
                  className={styles.validationError}
                  style={{
                    color: '#ff0000',
                    fontSize: '14px',
                    fontWeight: 'normal',
                    textAlign: 'center',

                    display: 'block',
                    width: '100%'
                  }}
                              >
                {nameError}
              </div>
              )}
              <div className={styles.questionInputWrapper}>
                <textarea
                  placeholder={locale.QUESTIONS_FORM_QUESTION_PLACEHOLDER || 'QUESTIONS_FORM_QUESTION_PLACEHOLDER'}
                  value={question}
                  onChange={(e) => {
                    setQuestion(e.target.value);
                    validateQuestion(e.target.value);
                  }}
                  className={styles.questionInput}
                  rows={4}
                />
                {isSubmitted ? (
                  <div className={styles.successMessage}>
                    {locale.QUESTIONS_FORM_SUCCESS_MESSAGE || 'QUESTIONS_FORM_SUCCESS_MESSAGE'}
                  </div>
                ) : (
                  <Button
                    type="submit"
                    size={ButtonSize.L}
                    disabled={!question.trim() || addQuestionMutation.isLoading}
                    className={`${styles.submitButton} ${question.trim() ? styles.submitButtonEnabled : ''}`}
                  >
                    {addQuestionMutation.isLoading 
                      ? (locale.QUESTIONS_FORM_SUBMIT_LOADING || 'QUESTIONS_FORM_SUBMIT_LOADING') 
                      : (locale.QUESTIONS_FORM_SUBMIT_BUTTON || 'QUESTIONS_FORM_SUBMIT_BUTTON')}
                  </Button>
                )}
              </div>
              {questionError && (
                <div 
                  className={styles.validationError}
                  style={{
                    color: '#ff0000',
                    fontSize: '14px',
                    fontWeight: 'normal',
                    textAlign: 'center',

                    display: 'block',
                    width: '100%'
                  }}
                              >
                {questionError}
              </div>
              )}
            </div>
          </form>
        </div>
      </div>
    );
    
    // Определяем что показывать в контенте
    const renderContent = () => {
      return (
        <div className={styles.questionsContainer}>
          <div className={styles.questionsList}>
              {questions.map((item) => (
                <ErrorBoundary key={`${item.name}-${item.datetime}`} fallback={null}>
                  <div className={styles.questionItem}>
                    <div className={styles.questionContent}>
                      <div className={styles.questionMeta}>
                        <span className={styles.questionAuthor}>{item.name}</span>
                        <span className={styles.questionDate}>{formatQuestionDate(item.datetime)}</span>
                      </div>
                      <p className={styles.questionText}>{item.text}</p>
                    </div>

                    {item.answer && (
                      <div className={styles.answer}>
                        <div className={styles.answerLine} />
                        <div className={styles.answerContent}>
                          <div className={styles.answerDate}>
                            {locale.QUESTIONS_ANSWER_FROM || 'QUESTIONS_ANSWER_FROM'} {item.answer_datetime && formatQuestionDate(item.answer_datetime)}
                          </div>
                          <div className={styles.answerHeader}>
                            <div className={styles.avatarWrapper}>
                              <div className={styles.avatar}>
                                <Image src={avatarImage} alt="Avatar" className={styles.avatarIcon} />
                              </div>
                            </div>
                            <span className={styles.answerAuthor}>Note Store</span>
                          </div>
                          <p className={styles.answerText}>{item.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </ErrorBoundary>
              ))}
            </div>
            {shouldShowPagination && (
              <QuestionsPagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                locale={{
                  PAGINATION_PREV: locale.PAGINATION_PREV,
                  PAGINATION_NEXT: locale.PAGINATION_NEXT,
                }}
              />
            )}
          </div>
      );
    };

    return (
      <div className={styles.container} ref={ref}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <H2 className={styles.title}>{locale.QUESTIONS_TITLE || 'QUESTIONS_TITLE'}</H2>
          </div>
          <div className={questions.length === 0 ? styles.contentEmpty : styles.content}>
            {questions.length === 0 ? (
              <>
                <div className={styles.emptyMessage}>{locale.QUESTIONS_EMPTY_MESSAGE || 'QUESTIONS_EMPTY_MESSAGE'}</div>
                {renderForm()}
              </>
            ) : (
              <>
                {renderContent()}
                {renderForm()}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ProductQuestions.displayName = 'ProductQuestions';

export default ProductQuestions; 